import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Switch, message } from "antd";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { putApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import { getOptionLabel } from "./questionOptions";
import QuestionInput from "./QuestionInput";
import QuestionTable from "./questionTable";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import {
  SUFFIX_SEP,
  TRIP_SEP,
  GuidanceBlock,
  evalCondition,
  getMultiselectDependency,
} from "./displayConditionUtils";



const QuestionCard = ({
  question,
  displayIndex,
  answers,
  setAnswers,
  handleAnswerChange,
  selectedQuestions,
  setSelectedQuestions,
  handleQuestionSelect,
  selectedSwitch,
  setSelectedSwitch,
  questionsList,
  optionsCache,
  setOptionsCache,
  tripIndex = null,
  optionSuffix = "",
  optionLabel = "",
  handleSwitchToggle,
}) => {
  let id = question.questionId;
  if (tripIndex !== null) id += `${TRIP_SEP}${tripIndex}`;
  if (optionSuffix) id += `_${optionSuffix}`;

  return (
    <div className="rounded-[10px] border border-[#D9E1E7] bg-white p-4">
      {/* //question title */}
      {question.questionTitle && (() => {
        const titles = Array.isArray(question.questionTitle)
          ? question.questionTitle
          : question.questionTitle ? [question.questionTitle] : [];
        const visibleTitles = titles.filter(
          (title) => title !== undefined && title !== null && title !== ""
        );
       
        if (visibleTitles.length === 0) return null;
       
        return (
          <div className="mb-2 space-y-1">
            {visibleTitles.map((title, index) => (
              <label
                key={`${String(title)}_${index}`}
                className="block text-[15px] font-semibold text-[#1F2937] leading-6 border-b border-gray-200 pb-1"
              >
                {title}
              </label>
            ))}
          </div>
        );
      })()}
      <GuidanceBlock guidance={question.guidance} />

      <div className="flex justify-between items-start gap-4 mb-3">
        <label className="flex items-start flex-1 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={!!selectedQuestions[id] || !!selectedQuestions[question.questionId]}
            onChange={() => {
              const newVal = !(!!selectedQuestions[id] || !!selectedQuestions[question.questionId]);
              if (handleQuestionSelect) {
                handleQuestionSelect(id, question.questionId, newVal);
              } else {
                setSelectedQuestions((prev) => ({
                  ...prev,
                  [id]: newVal,
                }));
              }
            }}
          />
          <span className="ml-3 text-[15px] font-semibold text-[#1F2937] leading-6">
            {displayIndex}. {question.questionText}
            {(selectedSwitch[id] || selectedSwitch[question.questionId]) && (
              <span className="text-red-500"> *</span>
            )}
          </span>
        </label>

        <Switch
          checked={!!selectedSwitch[id] || !!selectedSwitch[question.questionId]}
          onChange={(checked) =>
            handleSwitchToggle(id, question.questionId, checked)
          }
        />
      </div>

      {(tripIndex !== null || optionLabel) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {/* {tripIndex !== null && (
            <span className="inline-flex items-center rounded-full bg-[#D7F3EC] px-3 py-1 text-[12px] font-medium text-[#0F766E]">
              Trip {tripIndex + 1}
            </span>
          )} */}

          {/* {optionLabel && (
            <span className="inline-flex items-center rounded-full bg-[#EEF2FF] px-3 py-1 text-[12px] font-medium text-[#4338CA]">
              {optionLabel}
            </span>
          )} */}
        </div>
      )}

      {question.questionType === "table" ? (
        <QuestionTable
          question={question}
          tableId={id}
          answers={answers}
          setAnswers={setAnswers}
        />
      ) : (
        <QuestionInput
          field={question}
          value={answers[id]}
          onChange={(val) => handleAnswerChange(id, val)}
          onOptionsLoaded={(qid, opts) => {
            setOptionsCache((prev) => ({ ...prev, [qid]: opts }));
          }}
        />
      )}
    </div>
  );
};

const Division = ({
  divisionName,
  questions,
  answers,
  setAnswers,
  handleAnswerChange,
  selectedQuestions,
  setSelectedQuestions,
  handleQuestionSelect,
  selectedSwitch,
  questionsList,
  optionsCache,
  setOptionsCache,
  handleSwitchToggle,
}) => {
  const [open, setOpen] = useState(true);

  const renderChildren = (
    parentId,
    depth,
    tripIndex,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath,
  ) => {
    return questionsList
      .filter((child) => {
        if (!child.displayCondition) return false;

        const parentConditions = child.displayCondition.filter(
          (c) => c.triggerQuestionId === parentId,
        );
        if (parentConditions.length === 0) return false;

        const isSatisfied = (c) => {
          if (repeatingParentId && c.triggerQuestionId === parentId)
            return true;
          return evalCondition(c, answers, {
            tripIndex,
            optionSuffix,
            repeatingParentId,
          });
        };

        const logicalOp = (child.logicalOperator || "AND").toUpperCase();
        if (logicalOp === "OR") return parentConditions.some(isSatisfied);
        return parentConditions.some(isSatisfied);
      })
      .map((child, idx) => {
        let childSuffix = optionSuffix;
        if (child.displayCondition) {
          const specificCond = child.displayCondition.find(
            (c) =>
              c.triggerQuestionId === parentId &&
              c.operator === "eq" &&
              typeof c.value === "string",
          );
          if (specificCond) {
            childSuffix = optionSuffix
              ? `${optionSuffix}${SUFFIX_SEP}${specificCond.value}`
              : specificCond.value;
          }
        }

        return renderNode(
          child,
          depth,
          tripIndex,
          childSuffix,
          repeatingParentId,
          `${indexPath}.${idx + 1}`,
        );
      });
  };

  const renderNode = (
    question,
    depth = 0,
    tripIndex = null,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath = "1",
  ) => {
    const isVisible = evalCondition(question, answers, {
      tripIndex,
      optionSuffix,
      repeatingParentId,
    });
    if (!isVisible) return null;

    const multiselectTriggerId = getMultiselectDependency(
      question,
      answers,
      questionsList,
      tripIndex,
      optionSuffix,
    );

    if (multiselectTriggerId) {
      let items;
      if (answers[multiselectTriggerId]) items = answers[multiselectTriggerId];
      if (!items) {
        let s = optionSuffix;
        while (!items) {
          if (tripIndex !== null && s)
            items = answers[`${multiselectTriggerId}${TRIP_SEP}${tripIndex}_${s}`];
          if (!items && s) items = answers[`${multiselectTriggerId}_${s}`];
          if (!items && tripIndex !== null)
            items = answers[`${multiselectTriggerId}${TRIP_SEP}${tripIndex}`];
          if (!items) items = answers[multiselectTriggerId];

          if (items || !s || !s.includes(SUFFIX_SEP)) break;
          s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
        }
      }
      const actualSelectedItems = (items || []).filter((v) => v !== "");

      if (actualSelectedItems.length > 0) {
        return (
          <div key={`ms_${question.questionId}_${optionSuffix}`} className="space-y-4">
            {actualSelectedItems.map((itemValue, idx) => {
              const optionLabel = getOptionLabel(multiselectTriggerId, itemValue, optionsCache);

              return renderInstance(
                question,
                depth,
                tripIndex,
                optionSuffix
                  ? `${optionSuffix}${SUFFIX_SEP}${itemValue}`
                  : itemValue,
                repeatingParentId,
                `${indexPath}.${idx + 1}`,
                optionLabel,
              );
            })}
          </div>
        );
      }
    }

    return renderInstance(
      question,
      depth,
      tripIndex,
      optionSuffix,
      repeatingParentId,
      indexPath,
    );
  };

  const renderInstance = (
    question,
    depth,
    tripIndex,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath,
    optionLabel = "",
  ) => {
    const originalQid = question.questionId;
    let qid = originalQid;
    if (tripIndex !== null) qid += `${TRIP_SEP}${tripIndex}`;
    if (optionSuffix) qid += `_${optionSuffix}`;

    return (
      <div
        key={qid}
        className={`space-y-4 ${
          depth > 0 ? "ml-6 mt-3 border-l-4 border-l-teal-500 rounded-lg" : "mb-6"
        }`}
      >
        <QuestionCard
          question={question}
          displayIndex={indexPath}
          answers={answers}
          setAnswers={setAnswers}
          handleAnswerChange={handleAnswerChange}
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          handleQuestionSelect={handleQuestionSelect}
          selectedSwitch={selectedSwitch}
          questionsList={questionsList}
          optionsCache={optionsCache}
          setOptionsCache={setOptionsCache}
          tripIndex={tripIndex}
          optionSuffix={optionSuffix}
          optionLabel={optionLabel}
          handleSwitchToggle={handleSwitchToggle}
        />

        {/* --- GENERIC NUMBER REPETITION LOOP --- */}
        {(question.questionType === "number" ||
          originalQid === "number_of_trip_select") &&
        parseInt(answers[qid]) > 0 &&
        questionsList.some((child) =>
          child.displayCondition?.some(
            (c) => c.triggerQuestionId === originalQid,
          ),
        ) ? (
          <div className="mt-6 space-y-6">
            {Array.from(
              { length: Math.min(parseInt(answers[qid]), 100) },
              (_, i) => (
                <div
                  key={`${qid}_loop_${i}`}
                  className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                >
                  <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                    {originalQid.includes("trip") ||
                    originalQid.includes("journey")
                      ? "Trip"
                      : "Entry"}{" "}
                    {i + 1}
                  </div>
                  {renderChildren(
                    originalQid,
                    depth + 1,
                    i,
                    optionSuffix,
                    originalQid,
                    indexPath,
                  )}
                </div>
              ),
            )}
          </div>
        ) : (
          renderChildren(
            originalQid,
            depth + 1,
            tripIndex,
            optionSuffix,
            repeatingParentId,
            indexPath,
          )
        )}
      </div>
    );
  };

  const rootQuestions = questions.filter(
    (q) => !q?.displayCondition || q.displayCondition.length === 0,
  );

  return (
  <div className="mb-6 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
    <button
      onClick={() => setOpen(!open)}
      className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors duration-200 ${
        open ? "bg-teal-50 border-b border-teal-100" : "hover:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`font-semibold text-lg ${
            open ? "text-teal-700" : "text-gray-700"
          }`}
        >
          {divisionName}
        </span>
      </div>

      <svg
        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
          open ? "rotate-180" : ""
        }`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    {open && (
      <div className="p-6 space-y-4">
        {rootQuestions.map((question, index) =>
          renderNode(question, 0, null, null, null, `${index + 1}`)
        )}
      </div>
    )}
  </div>
);
};

export const QuestionForm = ({ formId, questions, formData, sectionId, subSectionId }) => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [selectedSwitch, setSelectedSwitch] = useState({});
  const [optionsCache, setOptionsCache] = useState({});
  const [popup, setPopup] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const handleQuestionSelect = (id, baseId, checked) => {
    setSelectedQuestions((prev) => {
      const newState = { ...prev, [id]: checked, [baseId]: checked };

      // Recursively find and select/deselect all child questions (Downward)
      const setChildrenRecursive = (currentBaseId) => {
        const children = questionsList.filter((q) =>
          q.displayCondition?.some(
            (c) => c.triggerQuestionId === currentBaseId
          )
        );
        children.forEach((child) => {
          newState[child.questionId] = checked;
          setChildrenRecursive(child.questionId);
        });
      };

      // Recursively select all parent questions (Upward) - Only on check
      const setParentsRecursive = (currentBaseId) => {
        const question = questionsList.find((q) => q.questionId === currentBaseId);
        if (question && question.displayCondition) {
          question.displayCondition.forEach((cond) => {
            if (cond.triggerQuestionId) {
              newState[cond.triggerQuestionId] = true;
              setParentsRecursive(cond.triggerQuestionId);
            }
          });
        }
      };

      if (checked) {
        setParentsRecursive(baseId);
      }
      setChildrenRecursive(baseId);
      return newState;
    });
  };
 
  const handleSwitchToggle = (id, baseId, checked) => {
    setSelectedSwitch((prev) => {
      const newState = { ...prev, [id]: checked, [baseId]: checked };
 
      const setChildrenMandatoryRecursive = (currentBaseId) => {
        const children = questionsList.filter((q) =>
          q.displayCondition?.some((c) => c.triggerQuestionId === currentBaseId)
        );
        children.forEach((child) => {
          newState[child.questionId] = checked;
          setChildrenMandatoryRecursive(child.questionId);
        });
      };
 
      setChildrenMandatoryRecursive(baseId);
      return newState;
    });
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  useEffect(() => {
    if (popup.open) {
      const timer = setTimeout(() => {
        setPopup((prev) => ({ ...prev, open: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [popup.open]);

  const questionsList = useMemo(() => {
    if (!questions) return [];
    let flat = [];
    questions.forEach((item) => {
      if (item?.questions && Array.isArray(item.questions)) {
        flat.push(...item.questions);
      } else if (item?.questionId) {
        flat.push(item);
      }
    });

    const uniqueMap = new Map();
    flat.forEach((q) => {
      const existing = uniqueMap.get(q.questionId);
      if (!existing) {
        uniqueMap.set(q.questionId, q);
      } else {
        if (
          (!existing.displayCondition || existing.displayCondition.length === 0) &&
          q.displayCondition &&
          q.displayCondition.length > 0
        ) {
          uniqueMap.set(q.questionId, q);
        }
      }
    });
    return Array.from(uniqueMap.values());
  }, [questions]);

  useEffect(() => {
    if (!formData?.questions?.length || !questionsList.length) return;

    const preselectedQuestions = {};
    const preselectedMandatory = {};

    formData.questions.forEach((item) => {
      const qid = typeof item === "object" ? item?.questionId : item;
      if (qid) {
        preselectedQuestions[qid] = true;
        preselectedMandatory[qid] = !!item.mandatory;
      }
    });

    setSelectedQuestions(preselectedQuestions);
    setSelectedSwitch(preselectedMandatory);
  }, [formData, questionsList]);

  const getBaseQuestionId = (id) => {
    const strId = String(id);

    // 1. Exact match in master list is always priority
    if (questionsList.some((q) => q.questionId === strId)) return strId;

    let base = strId;

    // 2. Surgical SUFFIX_SEP (:::) removal
    if (strId.includes(SUFFIX_SEP)) {
      base = strId.split(SUFFIX_SEP)[0];
    }

    // 3. Surgical TRIP_SEP (_trip_) removal
    // Only if followed by a numeric index (dynamic instance)
    const tripMatch = base.match(/(.*)_trip_(\d+)(.*)/);
    if (tripMatch) {
      // base becomes: (part before _trip_) + (any remaining suffix after the index)
      base = tripMatch[1] + (tripMatch[3] || "");
    }

    // 4. Final verification against master list
    const match = questionsList.find((q) => q.questionId === base);
    if (match) return match.questionId;

    // 5. Fallback to original strId if no matches were found after stripping
    // This prevents truncation for IDs that don't follow instance patterns
    return strId;
  };

  const getSelectedSectionIds = () => {
    const selectedBaseIds = new Set(
      Object.keys(selectedQuestions)
        .filter((id) => selectedQuestions[id])
        .map((id) => getBaseQuestionId(id)),
    );

    const sectionIds = new Set();
    questions.forEach((division) => {
      (division.questions || []).forEach((q) => {
        if (selectedBaseIds.has(q.questionId) && q.sectionId) {
          sectionIds.add(q.sectionId);
        }
      });
    });

    return Array.from(sectionIds);
  };

  const handleSubmit = async () => {
    const baseQuestionMap = new Map();
    Object.keys(selectedQuestions).forEach((id) => {
      if (selectedQuestions[id]) {
        const baseId = getBaseQuestionId(id);
        const isMandatory = !!selectedSwitch[id] || !!selectedSwitch[baseId];

        if (baseQuestionMap.has(baseId)) {
          const existing = baseQuestionMap.get(baseId);
          existing.mandatory = existing.mandatory || isMandatory;
        } else {
          const questionObj = questionsList.find((q) => q.questionId === baseId);
          let sec = sectionId || questionObj?.sectionId || questionObj?.section || "";
          let subsec = subSectionId || questionObj?.subSectionId || questionObj?.subsectionId || questionObj?.subsection || "";

          const preselectedItem = formData?.questions?.find(
            (q) => typeof q === "object" && q.questionId === baseId
          );

          if (preselectedItem) {
            sec = preselectedItem.section || preselectedItem.sectionId || sec;
            subsec = preselectedItem.subsection || preselectedItem.subSectionId || preselectedItem.subsectionId || subsec;
          }

          baseQuestionMap.set(baseId, {
            section: sec,
            subsection: subsec,
            questionId: baseId,
            mandatory: isMandatory,
          });
        }
      }
    });

    const addQuestions = Array.from(baseQuestionMap.values());
    const currentBaseIds = new Set(baseQuestionMap.keys());

    const originalQuestionIds = (formData?.questions || []).map(
      (q) => typeof q === "object" ? q?.questionId : q
    );

    const deleteQuestions = [];
    originalQuestionIds.forEach((qid) => {
      if (qid && !currentBaseIds.has(qid)) {
        deleteQuestions.push(qid);
      }
    });

    const payload = {
      ...formData,
      add: addQuestions,
      delete: deleteQuestions,
      sections: getSelectedSectionIds(),
    };

    try {
      const res = await putApi({
        apiUrl: API_URLS.SUBMIT_QUESTION(formId),
        data: payload,
      });

      if (res?.success) {
        setPopup({
          open: true,
          type: "success",
          title: "Success",
          message: res.message || "Saved successfully",
        });
        // setTimeout(() => {
        //   navigate(`/standard-forms/${res.data?._id || formId}`);
        // }, 1500);
      } else {
        setPopup({
          open: true,
          type: "error",
          title: "Error",
          message: res?.message || "Failed to save",
        });
      }
    } catch (error) {
      setPopup({
        open: true,
        type: "error",
        title: "Failed",
        message: "Failed to submit request",
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {questions.map((division) => (
        <Division
          key={division.divisionId}
          divisionName={division.divisionTitle}
          questions={division.questions || []}
          answers={answers}
          setAnswers={setAnswers}
          handleAnswerChange={handleAnswerChange}
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          handleQuestionSelect={handleQuestionSelect}
          selectedSwitch={selectedSwitch}
          handleSwitchToggle={handleSwitchToggle}
          questionsList={questionsList}
          optionsCache={optionsCache}
          setOptionsCache={setOptionsCache}
        />
      ))}

      <div className="flex justify-center gap-6 mt-8">
        <CustomButton
          variant="cancel"
          buttonText="Cancel"
          onClick={() => {
            setAnswers({});
            setSelectedQuestions({});
            setSelectedSwitch({});
          }}
        />
        <CustomButton
          variant="ok"
          buttonText="Save"
          onClick={handleSubmit}
        />
      </div>

      {popup.open && (
        <div className="fixed inset-0 bg-black/30 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <PopupCard
              type={popup.type}
              title={popup.title}
              message={popup.message}
            />
          </div>
        </div>
      )}
    </div>
  );
};
